// src/services/queries/dashboards/categoriesQueries.ts

import type { QueryFunction } from "@tanstack/react-query";
import { API_BASE_URL } from "@/services";
import type {
  CategoriesTreeResponse,
  CategoryFlat,
  CategoryCurrentSnapshot,
  CategoryChangesSnapshot,
  CategoryRollupsSnapshot,
  CategoryPerformanceVsBenchmarksWindowedResponse,
  CategoryHistoryCompactResponse,
} from "@/types/dashboards/categories";
import { queryKeys } from "@/constants/queryKeys";

export const fetchCategoriesTree: QueryFunction<CategoriesTreeResponse> = async ({ signal }) => {
  const init: RequestInit = {};
  if (signal) init.signal = signal;

  const res = await fetch(`${API_BASE_URL}/categories/tree`, init);
  if (!res.ok) throw new Error(`HTTP error! status: ${res.status}`);
  return res.json();
};

export const fetchCategoriesFlat: QueryFunction<CategoryFlat[]> = async ({ signal }) => {
  const init: RequestInit = {};
  if (signal) init.signal = signal;

  const res = await fetch(`${API_BASE_URL}/categories`, init);
  if (!res.ok) throw new Error(`HTTP error! status: ${res.status}`);
  return res.json();
};

export const categoriesTreeQueryOptions = () => ({
  queryKey: queryKeys.categories.tree(),
  queryFn: fetchCategoriesTree,
  staleTime: 1000 * 60 * 60, // 1h
});

export const categoriesFlatQueryOptions = () => ({
  queryKey: queryKeys.categories.list(),
  queryFn: fetchCategoriesFlat,
  staleTime: 1000 * 60 * 60, // 1h
});

// ===== New Category Snapshot Endpoints =====
export async function fetchCategoryCurrent(params: {
  categoryId?: number;
  signal?: AbortSignal;
}): Promise<CategoryCurrentSnapshot[]> {
  const sp = new URLSearchParams();
  if (params.categoryId !== undefined) sp.set("category_id", String(params.categoryId));
  const url = `${API_BASE_URL}/categories/current${sp.toString() ? `?${sp.toString()}` : ""}`;
  const init: RequestInit = params.signal ? { signal: params.signal } : {};
  const res = await fetch(url, init);
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.json();
}

export async function fetchCategoryCurrentChanges(params: {
  categoryId?: number;
  signal?: AbortSignal;
}): Promise<CategoryChangesSnapshot[]> {
  const sp = new URLSearchParams();
  if (params.categoryId !== undefined) sp.set("category_id", String(params.categoryId));
  const url = `${API_BASE_URL}/categories/current/changes${sp.toString() ? `?${sp.toString()}` : ""}`;
  const init: RequestInit = params.signal ? { signal: params.signal } : {};
  const res = await fetch(url, init);
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.json();
}

export async function fetchCategoryCurrentRatios(params: {
  categoryId?: number;
  signal?: AbortSignal;
}): Promise<CategoryCurrentSnapshot[]> {
  const sp = new URLSearchParams();
  if (params.categoryId !== undefined) sp.set("category_id", String(params.categoryId));
  const url = `${API_BASE_URL}/categories/current/ratios${sp.toString() ? `?${sp.toString()}` : ""}`;
  const init: RequestInit = params.signal ? { signal: params.signal } : {};
  const res = await fetch(url, init);
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.json();
}

export async function fetchCategoryCurrentRollups(params: {
  categoryId?: number;
  signal?: AbortSignal;
}): Promise<CategoryRollupsSnapshot[]> {
  const sp = new URLSearchParams();
  if (params.categoryId !== undefined) sp.set("category_id", String(params.categoryId));
  const url = `${API_BASE_URL}/categories/current/rollups${sp.toString() ? `?${sp.toString()}` : ""}`;
  const init: RequestInit = params.signal ? { signal: params.signal } : {};
  const res = await fetch(url, init);
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.json();
}

export const categoryCurrentQueryOptions = (categoryId?: number) => ({
  queryKey: queryKeys.categories.current(categoryId),
  queryFn: ({ signal }: { signal?: AbortSignal }) => {
    const args: { categoryId?: number; signal?: AbortSignal } = {};
    if (categoryId !== undefined) args.categoryId = categoryId;
    if (signal !== undefined) args.signal = signal;
    return fetchCategoryCurrent(args);
  },
  staleTime: 1000 * 60 * 5, // 5m
});

export const categoryCurrentChangesQueryOptions = (categoryId?: number) => ({
  queryKey: queryKeys.categories.currentChanges(categoryId),
  queryFn: ({ signal }: { signal?: AbortSignal }) => {
    const args: { categoryId?: number; signal?: AbortSignal } = {};
    if (categoryId !== undefined) args.categoryId = categoryId;
    if (signal !== undefined) args.signal = signal;
    return fetchCategoryCurrentChanges(args);
  },
  staleTime: 1000 * 60 * 5, // 5m
});

export const categoryCurrentRatiosQueryOptions = (categoryId?: number) => ({
  queryKey: queryKeys.categories.currentRatios(categoryId),
  queryFn: ({ signal }: { signal?: AbortSignal }) => {
    const args: { categoryId?: number; signal?: AbortSignal } = {};
    if (categoryId !== undefined) args.categoryId = categoryId;
    if (signal !== undefined) args.signal = signal;
    return fetchCategoryCurrentRatios(args);
  },
  staleTime: 1000 * 60 * 5, // 5m
});

export const categoryCurrentRollupsQueryOptions = (categoryId?: number) => ({
  queryKey: queryKeys.categories.currentRollups(categoryId),
  queryFn: ({ signal }: { signal?: AbortSignal }) => {
    const args: { categoryId?: number; signal?: AbortSignal } = {};
    if (categoryId !== undefined) args.categoryId = categoryId;
    if (signal !== undefined) args.signal = signal;
    return fetchCategoryCurrentRollups(args);
  },
  staleTime: 1000 * 60 * 5, // 5m
});

// ===== Category Performance vs Benchmarks (windowed) =====
export async function fetchCategoryPerformance(params: {
  categoryId: number;
  signal?: AbortSignal;
}): Promise<CategoryPerformanceVsBenchmarksWindowedResponse> {
  const sp = new URLSearchParams();
  sp.set("category_id", String(params.categoryId));
  const url = `${API_BASE_URL}/categories/performance?${sp.toString()}`;
  const init: RequestInit = params.signal ? { signal: params.signal } : {};
  const res = await fetch(url, init);
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.json();
}

export const categoryPerformanceQueryOptions = (categoryId: number) => ({
  queryKey: queryKeys.categories.performance(categoryId),
  queryFn: ({ signal }: { signal?: AbortSignal }) => {
    const args: { categoryId: number; signal?: AbortSignal } = { categoryId };
    if (signal !== undefined) args.signal = signal;
    return fetchCategoryPerformance(args);
  },
  staleTime: 1000 * 60 * 10, // 10m
});


// ===== Category history COMPACT (parallel arrays format - optimized) =====
export async function fetchCategoryHistoryCompact(params: {
  categoryId: number;
  metricKeys?: string[];
  start?: string;
  end?: string;
  signal?: AbortSignal;
}): Promise<CategoryHistoryCompactResponse> {
  const sp = new URLSearchParams();
  sp.set("category_id", String(params.categoryId));
  if (params.metricKeys && params.metricKeys.length > 0) {
    sp.set("metric_keys", params.metricKeys.join(","));
  }
  if (params.start) sp.set("start", params.start);
  if (params.end) sp.set("end", params.end);
  const url = `${API_BASE_URL}/categories/history/compact?${sp.toString()}`;
  const init: RequestInit = params.signal ? { signal: params.signal } : {};
  const res = await fetch(url, init);
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.json();
}

export const categoryHistoryCompactQueryOptions = (
  categoryId: number,
  metricKeys?: string[],
  start?: string,
  end?: string
) => ({
  queryKey: queryKeys.categories.historyCompact(categoryId, metricKeys || [], start, end),
  queryFn: ({ signal }: { signal?: AbortSignal }) => {
    const args: { categoryId: number; metricKeys?: string[]; start?: string; end?: string; signal?: AbortSignal } = { categoryId };
    if (metricKeys && metricKeys.length > 0) args.metricKeys = metricKeys;
    if (start) args.start = start;
    if (end) args.end = end;
    if (signal) args.signal = signal;
    return fetchCategoryHistoryCompact(args);
  },
  staleTime: 1000 * 60 * 10,
});

// ===== Total Market Cap (aggregated across all categories) =====
export async function fetchTotalMarketCapHistory(params: {
  signal?: AbortSignal;
}): Promise<{ dates: number[]; values: (number | null)[] }> {
  const url = `${API_BASE_URL}/categories/total/market_cap`;
  const init: RequestInit = params.signal ? { signal: params.signal } : {};
  const res = await fetch(url, init);
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.json();
}

export const totalMarketCapHistoryQueryOptions = () => ({
  queryKey: queryKeys.categories.totalMarketCapHistory(),
  queryFn: ({ signal }: { signal?: AbortSignal }) => {
    return fetchTotalMarketCapHistory(signal ? { signal } : {});
  },
  staleTime: 1000 * 60 * 10,
});

